import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { ExternalLink, X } from 'lucide-react';

const WHATSAPP_NUMERO = '5491122635392';

/**
 * CTA de contacto por WhatsApp con el comportamiento que ya usaba el resto de la app:
 * en mobile abre WhatsApp directo, en desktop muestra un QR para escanear con el celular
 * (en una compu, `wa.me` abre WhatsApp Web, que muchos usuarios no tienen logueado).
 *
 * Estaba duplicado en Paywall, Ayuda, VideoEditor, AddFirstProduct y SubscriptionLimitModal.
 * Se centralizó al necesitarlo en los planes a medida (ADR-664), donde el CTA de gestión de
 * suscripción es justamente este en vez del portal de Stripe.
 *
 * El modal va portaleado a `document.body` a propósito: el panel de contenido del desktop es
 * `position: fixed`, así que un overlay renderizado adentro queda POR DEBAJO de la sidebar.
 */
/**
 * Mensaje que viaja en el QR. Deliberadamente genérico y sin datos de la persona.
 *
 * El QR lo genera un servicio externo (api.qrserver.com) al que hay que mandarle el contenido
 * a codificar en la URL de la imagen. Todo lo que se ponga acá queda en los logs de un tercero.
 * Los mensajes personalizados llevan nombre, empresa, plan contratado y a veces que se quedó
 * sin créditos — o sea, quién es el cliente y qué le pasa. Eso NO sale de acá.
 *
 * Los links directos (el tap en mobile y el botón "Abrir WhatsApp Web") sí llevan el mensaje
 * completo: van derecho a WhatsApp, sin intermediario.
 */
const MENSAJE_QR_GENERICO = 'Buenas, me contactaba por mi plan de Atlas.';

/**
 * El mensaje con el que arranca un contacto a SOPORTE. Vive acá, al lado de
 * `urlsWhatsApp`, porque ya son tres las superficies que abren este canal —el
 * encabezado de desktop, la pastilla flotante de mobile y el título de Marca— y
 * cada una tenía o iba a tener su propia copia. Tres entradas al mismo chat
 * tienen que decir lo mismo.
 */
export const MENSAJE_SOPORTE = 'Buenas, me contactaba por mi cuenta de Atlas.';

/**
 * Las DOS urls de un contacto por WhatsApp: la directa y la del QR.
 *
 * Función pura y exportada para poder clavarla con tests —
 * ADR-2026-08-20-el-qr-de-whatsapp-es-un-componente-propio. La regla de privacidad de
 * acá arriba (el QR NUNCA lleva datos del cliente) era hasta ahora un `??` perdido en
 * el medio del render: cualquiera que armara la url del QR por su cuenta se la salteaba
 * sin enterarse. Ahora hay un solo lugar donde se decide, y un test que lo verifica.
 */
export function urlsWhatsApp({
  mensaje,
  mensajeQR,
}: {
  mensaje: string;
  mensajeQR?: string;
}): { directa: string; qr: string } {
  const arma = (texto: string) =>
    `https://wa.me/${WHATSAPP_NUMERO}?text=${encodeURIComponent(texto)}`;
  return {
    directa: arma(mensaje),
    // Ojo: esta url se le manda al generador externo. Ver MENSAJE_QR_GENERICO.
    qr: arma(mensajeQR ?? MENSAJE_QR_GENERICO),
  };
}

export interface ModalQRWhatsAppProps {
  abierto: boolean;
  onCerrar: () => void;
  /** Texto que precarga el link DIRECTO ("Abrir WhatsApp Web"). Puede llevar datos del cliente. */
  mensaje: string;
  /** Texto a codificar en el QR. Default: genérico. Ver `MENSAJE_QR_GENERICO`. */
  mensajeQR?: string;
  titulo?: string;
}

/**
 * El modal del QR, solo — ADR-2026-08-20-el-qr-de-whatsapp-es-un-componente-propio.
 *
 * Se separó de `ContactarPorWhatsApp` porque el encabezado de la home de desktop
 * necesita ESTE modal pero NO su botón: aquél trae un `estiloBase` fijo (pastilla azul
 * sólida de 40px) y el encabezado tiene su propia pinta (pastilla clara de 34px con
 * `ring`). Pisarlo con `className` no funciona de forma confiable —Tailwind resuelve
 * por orden en la hoja generada, no por orden en el string—, así que el camino es
 * consumir el modal y poner el trigger propio.
 *
 * `ContactarPorWhatsApp` quedó consumiendo esto, con su API pública intacta: sus cuatro
 * call sites (Paywall ×2, config_sections/Plan, CreditsPricingSheet) no se tocaron.
 *
 * Va portaleado a `document.body` a propósito: el panel de contenido del desktop es
 * `position: fixed`, así que un overlay renderizado adentro queda POR DEBAJO de la
 * sidebar.
 */
export const ModalQRWhatsApp: React.FC<ModalQRWhatsAppProps> = ({
  abierto,
  onCerrar,
  mensaje,
  mensajeQR,
  titulo = 'Contactanos por WhatsApp',
}) => {
  if (!abierto) return null;
  const { directa, qr } = urlsWhatsApp({ mensaje, mensajeQR });

  return createPortal(
    <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-surface-card rounded-lg shadow-md max-w-sm w-full p-5 relative">
        <button
          type="button"
          onClick={onCerrar}
          className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 transition-colors"
          aria-label="Cerrar"
        >
          <X className="w-5 h-5" />
        </button>
        <h3 className="text-[17px] font-semibold text-brand-black/90 mb-1.5 text-center">
          {titulo}
        </h3>
        <p className="text-[13px] text-neutral-500 mb-4 text-center">
          Escaneá el código QR con tu celular para abrir WhatsApp
        </p>
        <div className="flex justify-center mb-4">
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qr)}`}
            alt="Código QR de WhatsApp"
            className="w-48 h-48 rounded-md"
          />
        </div>
        <a
          href={directa}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full h-10 inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1fb855] text-white rounded-md font-medium text-[15px] shadow-sm transition-colors mb-2"
        >
          Abrir WhatsApp Web
          <ExternalLink className="w-4 h-4" />
        </a>
        <button
          type="button"
          onClick={onCerrar}
          className="w-full h-10 px-4 bg-surface-elevated border border-neutral-200 text-brand-black/90 rounded-md text-[15px] font-medium hover:bg-neutral-100 transition-colors"
        >
          Cerrar
        </button>
      </div>
    </div>,
    document.body,
  );
};

export interface ContactarPorWhatsAppProps {
  /** Texto que se precarga en el chat por los links DIRECTOS. Puede incluir datos del cliente. */
  mensaje: string;
  /**
   * Texto a codificar en el QR. Default: genérico. NO poner acá nombre, empresa, plan ni
   * estado de la cuenta — se lo lleva el generador externo de QR.
   */
  mensajeQR?: string;
  /** Label del botón. */
  children: React.ReactNode;
  /** Título del modal de QR (desktop). */
  tituloQR?: string;
  className?: string;
  /**
   * 'boton' (default): pill relleno con el estilo de marca. 'texto': link plano sin
   * ningún estilo propio — la apariencia la pone `className` (ej. la ayuda discreta
   * de /verificar-celular).
   */
  variante?: 'boton' | 'texto';
}

export const ContactarPorWhatsApp: React.FC<ContactarPorWhatsAppProps> = ({
  mensaje,
  mensajeQR,
  children,
  tituloQR = 'Contactanos por WhatsApp',
  className = '',
  variante = 'boton',
}) => {
  const [mostrarQR, setMostrarQR] = useState(false);
  const { directa: url } = urlsWhatsApp({ mensaje, mensajeQR });
  const estiloBase =
    'inline-flex items-center justify-center gap-2 h-10 px-4 rounded-md shadow-sm bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium transition-colors';
  const claseMobile =
    variante === 'texto'
      ? `md:hidden ${className}`
      : `md:hidden w-full sm:w-auto ${estiloBase} ${className}`;
  const claseDesktop =
    variante === 'texto'
      ? `hidden md:inline ${className}`
      : `hidden md:inline-flex ${estiloBase} ${className}`;

  return (
    <>
      {/* Mobile: al toque abre la app de WhatsApp. */}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={claseMobile}
      >
        {children}
      </a>
      {/* Desktop: QR para escanear desde el celular. */}
      <button
        type="button"
        onClick={() => setMostrarQR(true)}
        className={claseDesktop}
      >
        {children}
      </button>

      <ModalQRWhatsApp
        abierto={mostrarQR}
        onCerrar={() => setMostrarQR(false)}
        mensaje={mensaje}
        mensajeQR={mensajeQR}
        titulo={tituloQR}
      />
    </>
  );
};

export default ContactarPorWhatsApp;
